import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/utils/api';
import { Plus, UserCheck, CheckCircle } from 'lucide-react';
import Layout from '@/components/Layout';
import ModuleIntelligenceBlock from '@/components/ModuleIntelligenceBlock';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  assigned: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-purple-100 text-purple-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-100 text-gray-800'
};

const priorityColors = {
  low: 'bg-gray-100 text-gray-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700'
};

export default function Maintenance() {
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [units, setUnits] = useState([]);
  const [properties, setProperties] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [vendorDialogOpen, setVendorDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [assignVendorId, setAssignVendorId] = useState('');
  const [completeCost, setCompleteCost] = useState('');
  const [formData, setFormData] = useState({
    unit_id: '', property_id: '', tenant_id: '', title: '', title_ar: '',
    description: '', description_ar: '', priority: 'medium', category: 'general'
  });
  const [vendorForm, setVendorForm] = useState({
    name: '', service_type: 'general', phone: '', email: ''
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [reqRes, unitsRes, propsRes, vendorsRes, tenantsRes] = await Promise.all([
        api.getMaintenanceRequests(), api.getUnits(), api.getProperties(), api.getVendors(), api.getTenants()
      ]);
      setRequests(reqRes.data);
      setUnits(unitsRes.data);
      setProperties(propsRes.data);
      setVendors(vendorsRes.data);
      setTenants(tenantsRes.data);
    } catch (error) { console.error('Failed:', error); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const unit = units.find(u => u.id === formData.unit_id);
    try {
      await api.createMaintenanceRequest({
        ...formData,
        property_id: unit?.property_id || formData.property_id
      });
      setDialogOpen(false);
      setFormData({ unit_id: '', property_id: '', tenant_id: '', title: '', title_ar: '', description: '', description_ar: '', priority: 'medium', category: 'general' });
      fetchData();
    } catch (error) { console.error('Failed:', error); }
  };

  const handleAssign = async () => {
    if (!selectedRequest || !assignVendorId) return;
    try {
      await api.assignMaintenance(selectedRequest.id, assignVendorId);
      setAssignDialogOpen(false);
      fetchData();
    } catch (error) { console.error('Failed:', error); }
  };

  const handleComplete = async () => {
    if (!selectedRequest) return;
    try {
      await api.completeMaintenance(selectedRequest.id, parseFloat(completeCost) || 0);
      setCompleteDialogOpen(false);
      fetchData();
    } catch (error) { console.error('Failed:', error); }
  };

  const handleCreateVendor = async (e) => {
    e.preventDefault();
    try {
      await api.createVendor(vendorForm);
      setVendorDialogOpen(false);
      setVendorForm({ name: '', service_type: 'general', phone: '', email: '' });
      fetchData();
    } catch (error) { console.error('Failed:', error); }
  };

  const getUnitLabel = (id) => units.find(u => u.id === id)?.unit_number || '-';
  const getVendorName = (id) => vendors.find(v => v.id === id)?.name || '-';
  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '-';

  const filteredRequests = filterStatus === 'all' ? requests : requests.filter(r => r.status === filterStatus);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">{t('maintenance')}</h1>
          <div className="flex gap-2">
            <Dialog open={vendorDialogOpen} onOpenChange={setVendorDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline"><Plus className="mr-2 h-4 w-4" /> Add Vendor</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add Vendor</DialogTitle></DialogHeader>
                <form onSubmit={handleCreateVendor} className="space-y-4">
                  <div><Label>Name*</Label><Input value={vendorForm.name} onChange={(e) => setVendorForm({ ...vendorForm, name: e.target.value })} required /></div>
                  <div>
                    <Label>Service Type*</Label>
                    <Select value={vendorForm.service_type} onValueChange={(v) => setVendorForm({ ...vendorForm, service_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['plumbing', 'electrical', 'hvac', 'cleaning', 'painting', 'general'].map(s => (
                          <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>{t('phone')}*</Label><Input value={vendorForm.phone} onChange={(e) => setVendorForm({ ...vendorForm, phone: e.target.value })} required /></div>
                  <div><Label>{t('email')}</Label><Input type="email" value={vendorForm.email} onChange={(e) => setVendorForm({ ...vendorForm, email: e.target.value })} /></div>
                  <Button type="submit" className="w-full">{t('save')}</Button>
                </form>
              </DialogContent>
            </Dialog>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button><Plus className="mr-2 h-4 w-4" /> {t('addMaintenanceRequest')}</Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{t('addMaintenanceRequest')}</DialogTitle></DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{t('units')}*</Label>
                      <Select value={formData.unit_id} onValueChange={(v) => {
                        const unit = units.find(u => u.id === v);
                        setFormData({ ...formData, unit_id: v, property_id: unit?.property_id || '' });
                      }}>
                        <SelectTrigger><SelectValue placeholder="Select unit" /></SelectTrigger>
                        <SelectContent>{units.map(u => <SelectItem key={u.id} value={u.id}>{u.unit_number}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>{t('priority')}</Label>
                      <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['low', 'medium', 'high', 'urgent'].map(p => <SelectItem key={p} value={p}>{t(p)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>{t('category')}</Label>
                      <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['plumbing', 'electrical', 'hvac', 'cleaning', 'painting', 'general'].map(c => (
                            <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>{t('tenants')}</Label>
                      <Select value={formData.tenant_id} onValueChange={(v) => setFormData({ ...formData, tenant_id: v })}>
                        <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                        <SelectContent>{tenants.map(t => <SelectItem key={t.id} value={t.id}>{t.full_name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div><Label>{t('title')} (EN)*</Label><Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required /></div>
                  <div><Label>{t('title')} (AR)</Label><Input value={formData.title_ar} onChange={(e) => setFormData({ ...formData, title_ar: e.target.value })} /></div>
                  <div><Label>{t('description')} (EN)*</Label><Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required /></div>
                  <div><Label>{t('description')} (AR)</Label><Input value={formData.description_ar} onChange={(e) => setFormData({ ...formData, description_ar: e.target.value })} /></div>
                  <Button type="submit" className="w-full">{t('save')}</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-4 gap-4">
          <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-yellow-600">{requests.filter(r => r.status === 'pending').length}</div><div className="text-sm text-gray-500">{t('pending')}</div></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-blue-600">{requests.filter(r => r.status === 'assigned').length}</div><div className="text-sm text-gray-500">{t('assigned')}</div></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-purple-600">{requests.filter(r => r.status === 'in_progress').length}</div><div className="text-sm text-gray-500">{t('inProgress')}</div></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-green-600">{requests.filter(r => r.status === 'completed').length}</div><div className="text-sm text-gray-500">{t('completed')}</div></CardContent></Card>
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          {['all', 'pending', 'assigned', 'in_progress', 'completed'].map(s => (
            <Button key={s} variant={filterStatus === s ? 'default' : 'outline'} size="sm" onClick={() => setFilterStatus(s)}>
              {s === 'all' ? 'All' : s === 'in_progress' ? t('inProgress') : t(s)}
            </Button>
          ))}
        </div>

        <Card>
          <CardContent className="pt-6">
            {loading ? <div className="text-center py-8">Loading...</div> : filteredRequests.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No maintenance requests found.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('title')}</TableHead>
                    <TableHead>{t('units')}</TableHead>
                    <TableHead>{t('category')}</TableHead>
                    <TableHead>{t('priority')}</TableHead>
                    <TableHead>{t('status')}</TableHead>
                    <TableHead>{t('vendors')}</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRequests.map((req) => (
                    <TableRow key={req.id}>
                      <TableCell className="font-medium">{req.title}</TableCell>
                      <TableCell>{getUnitLabel(req.unit_id)}</TableCell>
                      <TableCell className="capitalize">{req.category}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${priorityColors[req.priority]}`}>{t(req.priority)}</span>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[req.status]}`}>
                          {req.status === 'in_progress' ? t('inProgress') : t(req.status)}
                        </span>
                      </TableCell>
                      <TableCell>{req.assigned_vendor_id ? getVendorName(req.assigned_vendor_id) : '-'}</TableCell>
                      <TableCell>{formatDate(req.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {req.status === 'pending' && (
                            <Button variant="outline" size="sm" onClick={() => { setSelectedRequest(req); setAssignDialogOpen(true); }}>
                              <UserCheck className="h-3 w-3 mr-1" /> Assign
                            </Button>
                          )}
                          {(req.status === 'assigned' || req.status === 'in_progress') && (
                            <Button variant="outline" size="sm" onClick={() => { setSelectedRequest(req); setCompleteDialogOpen(true); }}>
                              <CheckCircle className="h-3 w-3 mr-1" /> Complete
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Assign Dialog */}
        <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>{t('assignToVendor')}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <Select value={assignVendorId} onValueChange={setAssignVendorId}>
                <SelectTrigger><SelectValue placeholder="Select vendor" /></SelectTrigger>
                <SelectContent>{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name} ({v.service_type})</SelectItem>)}</SelectContent>
              </Select>
              <Button className="w-full" onClick={handleAssign}>Assign</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Complete Dialog */}
        <Dialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>{t('markAsCompleted')}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Actual Cost (SAR)</Label><Input type="number" value={completeCost} onChange={(e) => setCompleteCost(e.target.value)} /></div>
              <Button className="w-full" onClick={handleComplete}>Complete</Button>
            </div>
          </DialogContent>
        </Dialog>

        <ModuleIntelligenceBlock module="maintenance" testId="maintenance-intel" />
      </div>
    </Layout>
  );
}
